import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Check, X } from 'lucide-react';
import { useSpeechProviders } from '@/hooks/useSpeechProviders';
import { useToast } from '@/hooks/use-toast';
import { KEY_REENTRY_HELP, requiresKeyReentry } from '@/lib/transcriptionProviderLogic';
import {
  CUSTOM_SPEECH_NOTE, SPEECH_LANGUAGES, SPEECH_PRESETS, canSaveSpeech, canTestSpeech, describeSpeechError,
  describeSpeechTest, firstSpeechModel, mappedVoices, sameSpeechConfig,
  type SpeechPresetKey, type SpeechProvider, type SpeechTestResult, type TestedSpeechConfig, type VoiceDraft,
} from '@/lib/speechProviderLogic';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  provider?: SpeechProvider;
  /** Hook actions are shared with the parent list so both stay in sync. */
  actions: Pick<ReturnType<typeof useSpeechProviders>, 'testProvider' | 'saveProvider'>;
}

const blankDraft = (extra: VoiceDraft = {}): VoiceDraft => ({
  ...Object.fromEntries(SPEECH_LANGUAGES.map((l) => [l.code, { model: '', voice: '' }])),
  ...extra,
});

// Stored speech_config is untrusted JSON: keep only entries whose model and voice are strings (not trimmed, so edits round-trip).
const sanitizeVoices = (raw: unknown): VoiceDraft => {
  const out: VoiceDraft = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const [lang, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!v || typeof v !== 'object') continue;
    const { model, voice } = v as Record<string, unknown>;
    if (typeof model === 'string' && typeof voice === 'string') out[lang] = { model, voice };
  }
  return out;
};

export function SpeechProviderDialog({ open, onOpenChange, provider, actions }: Props) {
  const { toast } = useToast();
  const [label, setLabel] = useState('');
  const [preset, setPreset] = useState<SpeechPresetKey>('groq');
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [voices, setVoices] = useState<VoiceDraft>(blankDraft());
  const [test, setTest] = useState<{ result: SpeechTestResult; config: TestedSpeechConfig } | null>(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    setApiKey('');
    setTest(null);
    setTesting(false);
    setSaving(false);
    setFormError(null);
    if (!open) return;
    const p: SpeechPresetKey = !provider ? 'groq'
      : provider.preset === 'groq' || provider.preset === 'openai' || provider.preset === 'openrouter' ? provider.preset : 'custom';
    setPreset(p);
    setLabel(provider?.label ?? '');
    setBaseUrl(provider?.base_url ?? SPEECH_PRESETS.groq.baseUrl);
    setVoices(blankDraft(provider ? sanitizeVoices(provider.speech_config) : SPEECH_PRESETS.groq.suggested));
  }, [open, provider]);

  const current: TestedSpeechConfig = { baseUrl, apiKey, voices };
  const isEdit = !!provider;
  const fixed = preset !== 'custom';
  // Rows for the known languages plus any extra language keys already saved on the provider (for example a later en-IN).
  const rowCodes = [...SPEECH_LANGUAGES.map((l) => l.code), ...Object.keys(voices).filter((c) => !SPEECH_LANGUAGES.some((l) => l.code === c))];
  const rowLabel = (code: string) => SPEECH_LANGUAGES.find((l) => l.code === code)?.label ?? code;

  const setVoice = (code: string, field: 'model' | 'voice', value: string) => {
    setVoices((v) => ({ ...v, [code]: { ...(v[code] ?? { model: '', voice: '' }), [field]: value } }));
    setTest(null);
  };

  const handlePreset = (v: string) => {
    const key = v as SpeechPresetKey;
    setPreset(key);
    setBaseUrl(SPEECH_PRESETS[key].baseUrl);
    setVoices(blankDraft(SPEECH_PRESETS[key].suggested));
    setTest(null);
  };

  const handleTest = async () => {
    setTesting(true);
    setFormError(null);
    const config = { ...current };
    try {
      const result = await actions.testProvider({
        id: provider?.id, baseUrl: baseUrl.trim(), apiKey: apiKey || undefined,
        defaultModel: firstSpeechModel(voices), speechConfig: mappedVoices(voices),
      });
      setTest({ result, config });
    } catch (e) {
      setTest(null);
      setFormError((e as Error).message);
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!test?.result.testToken) return;
    setSaving(true);
    setFormError(null);
    try {
      await actions.saveProvider({
        id: provider?.id, label: label.trim(), preset, baseUrl: baseUrl.trim(), apiKey: apiKey || undefined,
        defaultModel: firstSpeechModel(voices), speechConfig: mappedVoices(voices), testToken: test.result.testToken,
      });
      toast({ title: provider ? `Updated ${label}` : `Added ${label}` });
      setApiKey('');
      setTest(null);
      onOpenChange(false);
    } catch (e) {
      const err = e as Error & { code?: string };
      setFormError(describeSpeechError(err.code, err.message));
    } finally {
      setSaving(false);
    }
  };

  const shown = test ? describeSpeechTest(test.result) : null;
  const dirty = !!test && !sameSpeechConfig(test.config, current);
  const needsKey = requiresKeyReentry({ isEdit, baseUrl, storedBaseUrl: provider?.base_url, apiKey });
  const testable = canTestSpeech({ baseUrl, apiKey, voices, isEdit, keyReentry: needsKey });
  const handleOpenChange = (o: boolean) => {
    if (!o && (saving || testing)) return;
    onOpenChange(o);
  };
  const presetNote = preset === 'custom' ? CUSTOM_SPEECH_NOTE : SPEECH_PRESETS[preset].note;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit speech provider' : 'Add speech provider'}</DialogTitle>
          <DialogDescription>
            Text-to-speech for Ask Masira spoken replies ("cloud voice"). Pick a voice per language, then test before saving.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="sp-label">Label</Label>
            <Input id="sp-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Groq voices" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="sp-preset">Preset</Label>
            <Select value={preset} onValueChange={handlePreset}>
              <SelectTrigger id="sp-preset"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.keys(SPEECH_PRESETS) as SpeechPresetKey[]).map((k) => (
                  <SelectItem key={k} value={k}>{SPEECH_PRESETS[k].label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {presetNote && <p className="text-xs text-muted-foreground">{presetNote}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="sp-base-url">Base URL</Label>
            <Input id="sp-base-url" value={baseUrl} onChange={(e) => { setBaseUrl(e.target.value.trim()); setTest(null); }}
              readOnly={fixed} aria-readonly={fixed} className={fixed ? 'bg-muted/50' : undefined}
              placeholder="https://tts.example.com/v1" />
          </div>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Voice per language</legend>
            <p className="text-xs text-muted-foreground">
              Leave a language blank to keep the on-device voice for it. A reply in a language with no cloud voice is read by the browser as before.
            </p>
            {rowCodes.map((code) => (
              <div key={code} className="grid grid-cols-[5rem_1fr_1fr] items-center gap-2">
                <span className="text-sm">{rowLabel(code)}</span>
                <Input aria-label={`${rowLabel(code)} model`} value={voices[code]?.model ?? ''}
                  onChange={(e) => setVoice(code, 'model', e.target.value)} placeholder="model" className="font-mono text-xs" />
                <Input aria-label={`${rowLabel(code)} voice`} value={voices[code]?.voice ?? ''}
                  onChange={(e) => setVoice(code, 'voice', e.target.value)} placeholder="voice" className="font-mono text-xs" />
              </div>
            ))}
          </fieldset>

          <div className="space-y-1.5">
            <Label htmlFor="sp-api-key">API key</Label>
            <Input id="sp-api-key" type="password" autoComplete="new-password" value={apiKey}
              onChange={(e) => { setApiKey(e.target.value); setTest(null); }}
              placeholder={provider?.api_key_last4 ? `•••• ${provider.api_key_last4}` : 'gsk_... / sk-...'}
              aria-describedby="sp-api-key-help" />
            <p id="sp-api-key-help" className="text-xs text-muted-foreground">
              {isEdit ? 'Leave blank to keep the current key. The key is write-only and is never shown again.' : 'Stored encrypted; never shown again after saving.'}
            </p>
          </div>

          <div className="space-y-2">
            <Button type="button" variant="outline" onClick={handleTest} disabled={testing || saving || !testable}
              aria-describedby={needsKey ? 'sp-key-reentry' : undefined}>
              {testing ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}
              Test connection
            </Button>
            {needsKey && <p id="sp-key-reentry" className="text-xs text-muted-foreground">{KEY_REENTRY_HELP}</p>}
            <div aria-live="polite" className="min-h-5 text-sm">
              {shown && (
                <div className={shown.ok ? 'text-success' : 'text-destructive'}>
                  <span className="flex items-center gap-1 font-medium">
                    {shown.ok ? <Check className="h-4 w-4" aria-hidden /> : <X className="h-4 w-4" aria-hidden />}
                    {shown.title}
                  </span>
                  {shown.detail && <span className="block text-xs">{shown.detail}</span>}
                  {shown.raw && (
                    <details className="mt-1 text-xs text-muted-foreground">
                      <summary className="cursor-pointer">Details</summary>
                      <pre className="mt-1 whitespace-pre-wrap break-all font-mono">{shown.raw}</pre>
                    </details>
                  )}
                </div>
              )}
              {dirty && <span className="block text-xs text-muted-foreground">Settings changed since the last test. Test again to enable Save.</span>}
              {formError && <span role="alert" className="block text-destructive">{formError}</span>}
            </div>
          </div>

          {isEdit && provider?.is_active && (
            <p className="rounded-md border border-warning/50 p-3 text-xs">
              This provider is active. Changes take effect within about a minute.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving || testing}>Cancel</Button>
          <Button type="button" onClick={handleSave}
            disabled={!canSaveSpeech({ label, test, current, saving, testing, keyReentry: needsKey })}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
